import { API_URL } from '../config/env'
import React, { useEffect, useState } from 'react'
import { Key, User, Save, Eye, EyeOff, CheckCircle, AlertTriangle, Trash2, Copy, Database } from 'lucide-react'
import { Button, Card, Input } from '../components/common'
import useSettingsStore from '../stores/settingsStore'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileSettingsPage from '../components/mobile/MobileSettingsPage'
import { diagnosePendingPhotos, deleteCorruptedPhoto, findDuplicatePhotos, cleanupDuplicatePhotos, clearAllPendingPhotos } from '../services/storageService'
import useSurveyStore from '../stores/surveyStore'
import { surveyApi } from '../services/apiService'

export default function SettingsPage() {
  const isMobile = useMobileLayout()
  const [surveyorName, setSurveyorName] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [osPlacesKey, setOsPlacesKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [saved, setSaved] = useState({ surveyor: false, apiKey: false, osKey: false })
  const [corruptedPhotos, setCorruptedPhotos] = useState([])
  const [diagnosing, setDiagnosing] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [duplicateReport, setDuplicateReport] = useState(null)
  const [findingDuplicates, setFindingDuplicates] = useState(false)
  const [cleaningDuplicates, setCleaningDuplicates] = useState(false)
  const [selectedSurveyForCleanup, setSelectedSurveyForCleanup] = useState('')
  const [serverCleaning, setServerCleaning] = useState(false)
  const [serverCleanupResult, setServerCleanupResult] = useState(null)

  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)
  const openaiApiKey = useSettingsStore(state => state.openaiApiKey)
  const loading = useSettingsStore(state => state.loading)
  const loadSettings = useSettingsStore(state => state.loadSettings)
  const setDefaultSurveyor = useSettingsStore(state => state.setDefaultSurveyor)
  const setOpenaiApiKey = useSettingsStore(state => state.setOpenaiApiKey)
  const refreshPendingCount = useSurveyStore(state => state.refreshPendingCount)
  const resetSyncStatus = useSurveyStore(state => state.resetSyncStatus)
  const surveys = useSurveyStore(state => state.surveys)
  const loadSurveys = useSurveyStore(state => state.loadSurveys)

  useEffect(() => {
    loadSettings()
    loadSurveys()
  }, [loadSettings, loadSurveys])

  useEffect(() => {
    setSurveyorName(defaultSurveyor)
    setApiKey(openaiApiKey)
    // Load OS Places key from localStorage
    const settings = localStorage.getItem('settings')
    if (settings) {
      try {
        const parsed = JSON.parse(settings)
        if (parsed.osPlacesApiKey) {
          setOsPlacesKey(parsed.osPlacesApiKey)
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [defaultSurveyor, openaiApiKey])

  // Mobile layout
  if (isMobile) {
    return <MobileSettingsPage />
  }

  const handleSaveSurveyor = async () => {
    try {
      await setDefaultSurveyor(surveyorName)
      setSaved(prev => ({ ...prev, surveyor: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, surveyor: false })), 2000)
    } catch (error) {
      console.error('Failed to save surveyor name:', error)
    }
  }

  const handleSaveApiKey = async () => {
    try {
      await setOpenaiApiKey(apiKey)
      setSaved(prev => ({ ...prev, apiKey: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, apiKey: false })), 2000)
    } catch (error) {
      console.error('Failed to save API key:', error)
    }
  }

  const handleSaveOSKey = async () => {
    try {
      // Save to localStorage
      const settings = localStorage.getItem('settings')
      const parsed = settings ? JSON.parse(settings) : {}
      parsed.osPlacesApiKey = osPlacesKey
      localStorage.setItem('settings', JSON.stringify(parsed))

      setSaved(prev => ({ ...prev, osKey: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, osKey: false })), 2000)
    } catch (error) {
      console.error('Failed to save OS Places API key:', error)
    }
  }

  const handleDiagnosePhotos = async () => {
    setDiagnosing(true)
    try {
      const pending = await diagnosePendingPhotos()
      console.log('All pending photos:', pending)

      // Filter for photos with invalid data (anything that's not a base64 string)
      const corrupted = pending.filter(p => {
        // Valid if it's a string (base64) or a Blob
        const isValid = p.imageDataType === 'string' || p.imageDataIsBlob
        return !isValid
      })

      setCorruptedPhotos(corrupted)
      if (corrupted.length === 0) {
        alert('No corrupted photos found. All pending photos have valid data.')
      }
    } catch (error) {
      console.error('Failed to diagnose photos:', error)
      alert('Failed to diagnose photos: ' + error.message)
    } finally {
      setDiagnosing(false)
    }
  }

  const handleDeleteCorruptedPhoto = async (photoId) => {
    if (!confirm('Are you sure you want to delete this corrupted photo? This cannot be undone.')) {
      return
    }

    setDeleting(photoId)
    try {
      await deleteCorruptedPhoto(photoId)
      setCorruptedPhotos(prev => prev.filter(p => p.id !== photoId))
      await refreshPendingCount()
      alert('Corrupted photo deleted successfully')
    } catch (error) {
      console.error('Failed to delete photo:', error)
      alert('Failed to delete photo: ' + error.message)
    } finally {
      setDeleting(null)
    }
  }

  const handleForceResetSync = async () => {
    if (!confirm('Force reset sync status? This will cancel any stuck sync operations.')) {
      return
    }

    try {
      await resetSyncStatus()
      alert('Sync status reset successfully')
    } catch (error) {
      console.error('Failed to reset sync:', error)
      alert('Failed to reset sync: ' + error.message)
    }
  }

  const handleFindDuplicates = async () => {
    setFindingDuplicates(true)
    try {
      const report = await findDuplicatePhotos()
      console.log('Duplicate analysis:', report)
      setDuplicateReport(report)
    } catch (error) {
      console.error('Failed to find duplicates:', error)
      alert('Failed to find duplicates: ' + error.message)
    } finally {
      setFindingDuplicates(false)
    }
  }

  const handleCleanupDuplicates = async (dryRun = true) => {
    if (!dryRun && !confirm('This will permanently delete duplicate photos. Are you sure?')) {
      return
    }

    setCleaningDuplicates(true)
    try {
      const result = await cleanupDuplicatePhotos(dryRun)
      console.log('Cleanup result:', result)

      if (dryRun) {
        alert(`Found ${result.wouldDelete} duplicate photos that can be removed.\n\nClick "Clean Up Now" to delete them.`)
      } else {
        alert(`Successfully deleted ${result.deleted} duplicate photos!`)
        await refreshPendingCount()
        // Refresh the duplicate report
        await handleFindDuplicates()
      }
    } catch (error) {
      console.error('Failed to cleanup duplicates:', error)
      alert('Failed to cleanup duplicates: ' + error.message)
    } finally {
      setCleaningDuplicates(false)
    }
  }

  const handleClearAllPending = async () => {
    const confirmed = window.confirm(
      '⚠️ EMERGENCY ACTION ⚠️\n\n' +
      'This will DELETE ALL pending photos that haven\'t been uploaded to R2.\n\n' +
      'This is useful if you have hundreds of duplicate photos that are stuck in sync.\n\n' +
      'Photos that are already uploaded will NOT be affected.\n\n' +
      'Are you absolutely sure you want to continue?'
    )

    if (!confirmed) return

    setCleaningDuplicates(true)
    try {
      const result = await clearAllPendingPhotos()
      console.log('Clear pending result:', result)
      alert(`Successfully deleted ${result.deleted} pending photos.\n${result.remaining} uploaded photos remain.`)
      await refreshPendingCount()
      // Refresh the duplicate report
      if (duplicateReport) {
        await handleFindDuplicates()
      }
    } catch (error) {
      console.error('Failed to clear pending photos:', error)
      alert('Failed to clear pending photos: ' + error.message)
    } finally {
      setCleaningDuplicates(false)
    }
  }

  const handleServerCleanup = async () => {
    const confirmed = window.confirm(
      '⚠️ SERVER CLEANUP ⚠️\n\n' +
      'This will PERMANENTLY delete duplicate photos from the server (R2 and database).\n\n' +
      'Categories with more than 10 photos will be reduced to keep only the first 10 photos (by capture time).\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?'
    )

    if (!confirmed) return

    setServerCleaning(true)
    setServerCleanupResult(null)
    try {
      const response = await fetch(
        `/api/surveys//cleanup-duplicates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Server cleanup failed')
      }

      const result = await response.json()
      console.log('Server cleanup result:', result)
      setServerCleanupResult({ success: true, ...result })

      // Reload surveys to get updated photo counts
      await loadSurveys()
    } catch (error) {
      console.error('Failed to cleanup server duplicates:', error)
      setServerCleanupResult({ success: false, error: error.message })
    } finally {
      setServerCleaning(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">
          Configure your application preferences
        </p>
      </div>

      {/* Default Surveyor */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Default Surveyor</h2>
            <p className="text-sm text-slate-500 mt-1">
              This name will be pre-filled when creating new surveys
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            label="Surveyor Name"
            value={surveyorName}
            onChange={(e) => setSurveyorName(e.target.value)}
            placeholder="Enter default surveyor name"
          />
          <Button
            variant="primary"
            onClick={handleSaveSurveyor}
            loading={loading}
            icon={saved.surveyor ? 'CheckCircle' : 'Save'}
          >
            {saved.surveyor ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </Card>

      {/* OpenAI API Key */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Key className="w-6 h-6 text-accent-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">OpenAI API Key</h2>
            <p className="text-sm text-slate-500 mt-1">
              Required for AI-powered photo analysis using ChatGPT Vision
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Your API key is stored locally and never sent to our servers.
              Get your API key from{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                OpenAI Platform
              </a>
            </p>
          </div>

          {!apiKey && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> AI analysis is currently disabled. Add your OpenAI API key to enable the "Analyse Photos" feature.
              </p>
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleSaveApiKey}
            loading={loading}
            icon={saved.apiKey ? 'CheckCircle' : 'Save'}
          >
            {saved.apiKey ? 'Saved!' : 'Save API Key'}
          </Button>
        </div>
      </Card>

      {/* OS Places API Key */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Key className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">OS Places API Key (Optional)</h2>
            <p className="text-sm text-slate-500 mt-1">
              For accurate UK address lookups (Ordnance Survey)
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              API Key
            </label>
            <input
              type="text"
              value={osPlacesKey}
              onChange={(e) => setOsPlacesKey(e.target.value)}
              placeholder="Optional - improves UK address search accuracy"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
            />
            <p className="text-sm text-slate-500 mt-2">
              Get a free API key (250 requests/day) from{' '}
              <a
                href="https://osdatahub.os.uk/plans"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                OS Data Hub
              </a>
              {' '}for more accurate address autocomplete.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Without this key, the app will use OpenStreetMap (less accurate for UK addresses).
            </p>
          </div>

          <Button
            variant="primary"
            onClick={handleSaveOSKey}
            loading={loading}
            icon={saved.osKey ? 'CheckCircle' : 'Save'}
          >
            {saved.osKey ? 'Saved!' : 'Save API Key'}
          </Button>
        </div>
      </Card>

      {/* Developer Tools - Corrupted Photo Cleanup */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Developer Tools</h2>
            <p className="text-sm text-slate-500 mt-1">
              Diagnose and fix corrupted photos that can't sync
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDiagnosePhotos}
              loading={diagnosing}
              icon="AlertTriangle"
              className="flex-1"
            >
              {diagnosing ? 'Scanning...' : 'Scan for Corrupted Photos'}
            </Button>
            <Button
              variant="outline"
              onClick={handleForceResetSync}
              icon="AlertTriangle"
              className="flex-1 border-orange-300 text-orange-700 hover:bg-orange-50"
            >
              Force Reset Sync
            </Button>
          </div>

          {corruptedPhotos.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium mb-3">
                Found {corruptedPhotos.length} corrupted photo{corruptedPhotos.length !== 1 ? 's' : ''}:
              </p>
              <div className="space-y-2">
                {corruptedPhotos.map((photo) => (
                  <div key={photo.id} className="flex items-center justify-between bg-white p-3 rounded border border-red-200">
                    <div className="text-sm">
                      <div className="font-medium text-slate-900">
                        Photo #{photo.id} - Category {photo.categoryCode}
                      </div>
                      <div className="text-slate-500 text-xs mt-1">
                        Type: {photo.imageDataConstructor || 'unknown'} | Captured: {new Date(photo.capturedAt).toLocaleString()}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => handleDeleteCorruptedPhoto(photo.id)}
                      loading={deleting === photo.id}
                      icon="Trash2"
                      className="text-red-600 hover:bg-red-100"
                    >
                      Delete
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Duplicate Photo Cleanup */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Copy className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Duplicate Photo Cleanup</h2>
            <p className="text-sm text-slate-500 mt-1">
              Find and remove duplicate photos from local storage
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleFindDuplicates}
                loading={findingDuplicates}
                icon="Copy"
                className="flex-1"
              >
                {findingDuplicates ? 'Scanning...' : 'Find Duplicates'}
              </Button>
              {duplicateReport && duplicateReport.duplicateGroups > 0 && (
                <Button
                  variant="outline"
                  onClick={() => handleCleanupDuplicates(false)}
                  loading={cleaningDuplicates}
                  icon="Trash2"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                >
                  {cleaningDuplicates ? 'Cleaning...' : 'Clean Up Now'}
                </Button>
              )}
            </div>

            {duplicateReport && duplicateReport.pendingPhotos > 50 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm font-medium mb-2">
                  ⚠️ {duplicateReport.pendingPhotos} photos pending - This seems unusually high!
                </p>
                <Button
                  variant="outline"
                  onClick={handleClearAllPending}
                  loading={cleaningDuplicates}
                  icon="Trash2"
                  fullWidth
                  className="border-red-400 text-red-800 hover:bg-red-100 font-semibold"
                >
                  {cleaningDuplicates ? 'Clearing...' : 'Emergency: Clear ALL Pending Photos'}
                </Button>
                <p className="text-xs text-red-600 mt-2">
                  This will delete all {duplicateReport.pendingPhotos} pending photos. Already uploaded photos are safe.
                </p>
              </div>
            )}
          </div>

          {duplicateReport && (
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-2xl font-bold text-slate-900">{duplicateReport.totalPhotos}</div>
                  <div className="text-xs text-slate-500">Total Photos</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{duplicateReport.pendingPhotos}</div>
                  <div className="text-xs text-slate-500">Pending Upload</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{duplicateReport.uploadedPhotos}</div>
                  <div className="text-xs text-slate-500">Uploaded</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">{duplicateReport.duplicateGroups}</div>
                  <div className="text-xs text-slate-500">Duplicate Groups</div>
                </div>
              </div>

              {duplicateReport.duplicateGroups > 0 || (duplicateReport.suspiciousCategories && duplicateReport.suspiciousCategories.length > 0) ? (
                <div className="mt-4 space-y-4">
                  {duplicateReport.duplicateGroups > 0 && (
                    <div>
                      <p className="text-blue-800 text-sm font-medium mb-2">
                        Found {duplicateReport.duplicateGroups} groups of duplicate photos
                      </p>
                      <div className="text-xs text-slate-600 space-y-1 max-h-48 overflow-y-auto">
                        {duplicateReport.duplicates.slice(0, 10).map((dup, idx) => (
                          <div key={idx} className="bg-white p-2 rounded border border-slate-200">
                            <div className="font-medium">
                              {dup.type === 'remoteId' ? 'Remote ID' : dup.type === 'r2Key' ? 'R2 Key' : 'Captured At'}: {dup.key.slice(0, 30)}...
                            </div>
                            <div className="text-slate-500">
                              {dup.count} copies - Photos: {dup.photos.map(p => `#${p.id}`).join(', ')}
                            </div>
                          </div>
                        ))}
                        {duplicateReport.duplicates.length > 10 && (
                          <div className="text-center text-slate-500">
                            ...and {duplicateReport.duplicates.length - 10} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {duplicateReport.suspiciousCategories && duplicateReport.suspiciousCategories.length > 0 && (
                    <div>
                      <p className="text-orange-800 text-sm font-medium mb-2">
                        ⚠️ Suspicious categories with many photos (may indicate duplicates):
                      </p>
                      <div className="text-xs text-slate-600 space-y-1 max-h-48 overflow-y-auto">
                        {duplicateReport.suspiciousCategories.map((cat, idx) => (
                          <div key={idx} className="bg-white p-2 rounded border border-orange-200">
                            <div className="font-medium">
                              {cat.key} - {cat.count} photos
                            </div>
                            <div className="text-slate-500">
                              {cat.uploaded} uploaded, {cat.pending} pending
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-green-700 text-sm font-medium mt-2">
                  No duplicate photos found! ✓
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Server-Side Duplicate Cleanup */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Database className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Server Duplicate Cleanup</h2>
            <p className="text-sm text-slate-500 mt-1">
              Remove duplicate photos from server storage (keeps first 10 per category)
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-purple-800 text-sm">
              <strong>Warning:</strong> This will permanently delete duplicate photos from the server.
              Categories with more than 10 photos will be reduced to keep only the first 10 photos (by capture time).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Select Survey to Clean
            </label>
            <select
              value={selectedSurveyForCleanup}
              onChange={(e) => setSelectedSurveyForCleanup(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Choose a survey...</option>
              {surveys.map((survey) => (
                <option key={survey.id} value={survey.id}>
                  {survey.propertyName} - {survey.address}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="outline"
            onClick={handleServerCleanup}
            loading={serverCleaning}
            disabled={!selectedSurveyForCleanup}
            icon="Trash2"
            fullWidth
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            {serverCleaning ? 'Cleaning Server Duplicates...' : 'Clean Server Duplicates'}
          </Button>

          {serverCleanupResult && (
            <div className={`p-4 border rounded-lg ${serverCleanupResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              {serverCleanupResult.success ? (
                <>
                  <p className="text-green-800 text-sm font-medium mb-2">
                    ✓ Successfully cleaned {serverCleanupResult.deleted} duplicate photos!
                  </p>
                  <p className="text-green-700 text-xs mb-2">
                    Remaining: {serverCleanupResult.remaining} photos
                  </p>
                  {serverCleanupResult.categoryCounts && (
                    <div className="text-xs text-green-700 space-y-1">
                      <p className="font-medium">Category breakdown:</p>
                      {Object.entries(serverCleanupResult.categoryCounts).map(([code, counts]) => (
                        <div key={code} className="pl-2">
                          {code}: {counts.original} → {counts.remaining} photos
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-red-800 text-sm">
                  ✗ Failed: {serverCleanupResult.error}
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* About */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">About</h2>
        <div className="space-y-2 text-sm text-slate-600">
          <p><strong>Clystcore Surveyors</strong> - Property Survey Application</p>
          <p>Version 1.0.0</p>
          <p className="mt-4">
            A professional iPad-optimized application for capturing and analysing property survey photos with AI-powered insights.
          </p>
        </div>
      </Card>
    </div>
  )
}
